// FILE: src/components/ResultsPage.js
// Shown when a session ends (natural completion, early finish, or time expiry).
// Displays a scored summary, per-question review list, and filter controls.
//
// ── Data model ────────────────────────────────────────────────────────────────
//
//   computeResults(questions, questionStates, skippedIndices)
//
//   For each question index i, status is determined in priority order:
//
//     correct     questionStates[i].submitted === true
//                 AND selected answers exactly match correctAnswers
//
//     incorrect   questionStates[i].submitted === true
//                 AND answers do NOT match
//
//     skipped     not submitted AND index is still in skippedIndices at
//                 session end.  Only possible after early finish or time
//                 expiry — natural completion drains skippedIndices to zero.
//
//     unanswered  not submitted AND index is NOT in skippedIndices.
//                 Covers: never-visited questions, in-progress questions at
//                 session end, and questions dismissed from the revisit queue
//                 (via Skip during the revisit pass) without being answered.
//
//   percentage = Math.round(correct / total * 100)
//                All questions (including skipped/unanswered) form the
//                denominator, so only answered-correctly questions score.
//
// ── Props ─────────────────────────────────────────────────────────────────────
//
//   session           {object}   active session descriptor (displayName,
//                                modeName, totalQuestions, durationMinutes,
//                                questions[])
//   questionStates    {object}   { [index]: { selectedAnswers, submitted } }
//                                populated by App.js snapshot helpers
//   skippedIndices    {Set}      question indices still in the skip queue at
//                                session end (reflects true App.js state)
//   completionReason  {string}   'completed' | 'early' | 'expired'
//   onReset           {function} called when the user clicks "Return to Home"
//   onRetake          {function} called when the user clicks "Retake Exam"

import React, { useState } from 'react';
import PhasePerformance from '../journey/PhasePerformance';
import TargetedQuizModal from './TargetedQuizModal';
import SessionStartStatus from '../shared/SessionStartStatus';
import { computePhaseMetrics, generateInsights, getWeakPhases } from '../../utils/phaseAnalytics';
import { getExamConfig, getPaceConfig } from '../../config/exams';
import { computePaceAnalytics, isPaceEnabled } from '../../services/paceService';
import PaceAnalytics from '../pace/PaceAnalytics';
import CoachLayout from '../coach/CoachLayout';
import ScreenHeader from '../shared/ScreenHeader';
import DiscordPromo from '../shared/DiscordPromo';
import { useLearnerAuth } from '../../contexts/LearnerAuthContext';
import { caseScoreStats } from '../case-studies/exam/CaseReviewGroup';
import SessionReviewList from './SessionReviewList';
import { buildLiveReviewModel } from './reviewModel';
import { getQuestionType } from '../../questionTypes';
import { updateLearnerProfile } from '../../services/learnerProfileService';
import rcss from './ResultsPage.module.css';

// ── Data model ────────────────────────────────────────────────────────────────

/**
 * Computes per-question result records and aggregate counts.
 * Exported so it can be unit-tested independently of the component.
 *
 * @param   {object[]} questions      session.questions
 * @param   {object}   questionStates { [index]: { selectedAnswers, submitted } }
 * @param   {Set}      skippedIndices indices still in the skip queue at session end
 * @returns {{ items, correct, incorrect, skipped, unanswered, total, percentage }}
 */
export function computeResults(questions, questionStates, skippedIndices) {
  const items = questions.map((q, i) => {
    const state = questionStates[i];
    const submitted = state?.submitted === true;
    const selectedAnswers = state?.selectedAnswers ?? [];

    let status;
    if (submitted) {
      // Registry scoring when the type defines it (order-aware for
      // ordered_sequence, pair-aware for enhanced_matching); the legacy
      // set-equality compare remains the fallback and matches the registry's
      // semantics for classic types.
      const def = getQuestionType(q.type);
      const isCorrect = def
        ? def.score(q, selectedAnswers)
        : (selectedAnswers.length === q.correctAnswers.length &&
           q.correctAnswers.every((id) => selectedAnswers.includes(id)));
      status = isCorrect ? 'correct' : 'incorrect';
    } else if (skippedIndices.has(i)) {
      status = 'skipped';
    } else {
      status = 'unanswered';
    }

    // Phase 2: per-item experimental flag.  Carries through to the review list
    // so callers can render or hide an indicator if they want; not shown to
    // learners by default.
    const isExperimental = q?.isExperimental === true;

    return { index: i, question: q, selectedAnswers, submitted, status, isExperimental };
  });

  // ── Scored-only aggregates ────────────────────────────────────────────
  // Phase 2: experimental questions are shown to the learner but excluded
  // from every score calculation — correct, incorrect, skipped, unanswered,
  // total denominator, and percentage.  They remain in `items` so the
  // per-question review list can still show explanations on every question
  // the learner saw.
  const scoredItems  = items.filter((r) => !r.isExperimental);
  const correct      = scoredItems.filter((r) => r.status === 'correct').length;
  const incorrect    = scoredItems.filter((r) => r.status === 'incorrect').length;
  const skipped      = scoredItems.filter((r) => r.status === 'skipped').length;
  const unanswered   = scoredItems.filter((r) => r.status === 'unanswered').length;
  const total        = scoredItems.length;
  const percentage   = total > 0 ? Math.round((correct / total) * 100) : 0;

  // True when the session contained any experimental questions.  Drives the
  // subtle "some questions may be unscored" note on the results page.
  const hasExperimental = items.some((r) => r.isExperimental);

  return { items, correct, incorrect, skipped, unanswered, total, percentage, hasExperimental };
}

/**
 * Returns a performance feedback message based on the score percentage.
 * Exported so it can be unit-tested independently of the component.
 *
 * @param   {number} percentage  Score from 0–100
 * @returns {string}             Feedback message
 */
export function getPerformanceFeedback(percentage) {
  if (percentage >= 80) {
    return "Great work — you're ready to take the exam!";
  }
  if (percentage >= 60) {
    return 'Good work, but more practice is needed before you are fully ready.';
  }
  return 'Continue reviewing course content before attempting the exam again.';
}

// ── Constants ─────────────────────────────────────────────────────────────────

// Headings are kept as plain text (no inline emoji) so that
// getByText('exact string') in tests matches the element's textContent
// directly.  Emoji are rendered in a separate sibling element.
const REASON_CFG = {
  completed: { icon: '🎉', text: 'Session Complete!', color: 'var(--good)' },
  early:     { icon: '📋', text: 'Exam Ended',        color: 'var(--warn)' },
  expired:   { icon: '⏰', text: "Time's Up",          color: 'var(--bad)' },
};

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  // ── Page shell ───────────────────────────────────────────────────────────────
  page: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    background: 'var(--bg)',
    padding: '32px 16px',
  },
  container: {
    maxWidth: '760px',
    margin: '0 auto',
  },

  // ── Header action buttons (rendered inside ScreenHeader) ─────────────────────
  retakeButton: {
    padding: '10px 20px',
    fontSize: '0.92rem',
    background: 'var(--brand)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#fff',
    fontWeight: '600',
  },
  targetedButton: {
    padding: '10px 20px',
    fontSize: '0.92rem',
    background: '#fff',
    border: '2px solid var(--bad)',
    borderRadius: '6px',
    cursor: 'pointer',
    color: 'var(--bad)',
    fontWeight: '600',
  },

  // ── Score hero card ───────────────────────────────────────────────────────────
  heroCard: {
    background: '#fff',
    borderRadius: '12px',
    padding: '28px 24px',
    marginBottom: '20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    textAlign: 'center',
  },
  heroIcon: {
    fontSize: '2.2rem',
    lineHeight: 1,
    marginBottom: '6px',
  },
  heroHeading: (color) => ({
    fontSize: '1.7rem',
    fontWeight: 'bold',
    color,
    margin: '0 0 6px',
  }),
  heroMeta: {
    color: 'var(--muted)',
    fontSize: '0.95rem',
    margin: '0 0 20px',
  },
  scoreCircle: (color) => ({
    width: '110px',
    height: '110px',
    borderRadius: '50%',
    border: `6px solid ${color}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  }),
  scoreCircleText: (color) => ({
    fontSize: '2rem',
    fontWeight: 'bold',
    color,
    lineHeight: 1,
  }),
  feedbackBanner: (color, bg) => ({
    display: 'inline-block',
    padding: '10px 20px',
    borderRadius: '8px',
    background: bg,
    color,
    fontWeight: '600',
    fontSize: '0.92rem',
    border: `1px solid ${color}`,
    maxWidth: '520px',
  }),

  // ── Stats grid ────────────────────────────────────────────────────────────────
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px',
    marginBottom: '28px',
  },
  statCard: (borderColor) => ({
    background: '#fff',
    border: `2px solid ${borderColor}`,
    borderRadius: '8px',
    padding: '14px 8px',
    textAlign: 'center',
  }),
  statNumber: (color) => ({
    fontSize: '1.8rem',
    fontWeight: 'bold',
    color,
    lineHeight: 1,
    marginBottom: '4px',
  }),
  statLabel: {
    fontSize: '0.72rem',
    color: 'var(--muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // ── Filter row ────────────────────────────────────────────────────────────────
  filterRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  filterButton: (isActive, color) => ({
    padding: '6px 14px',
    borderRadius: '20px',
    border: `2px solid ${isActive ? color : 'var(--line-strong)'}`,
    background: isActive ? color : '#fff',
    color: isActive ? '#fff' : 'var(--muted)',
    fontSize: '0.82rem',
    fontWeight: isActive ? '700' : 'normal',
    cursor: 'pointer',
    transition: 'all 0.12s',
  }),

  // ── Review list ───────────────────────────────────────────────────────────────
  reviewList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '32px',
  },
  emptyMessage: {
    textAlign: 'center',
    color: 'var(--line-strong)',
    padding: '32px',
    fontStyle: 'italic',
  },

  // ── Individual review item ────────────────────────────────────────────────────
  reviewItem: (borderColor) => ({
    background: '#fff',
    border: `1px solid ${borderColor}`,
    borderLeft: `4px solid ${borderColor}`,
    borderRadius: '6px',
    overflow: 'hidden',
    cursor: 'pointer',
  }),
  reviewItemHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 14px',
  },
  qNumber: {
    fontSize: '0.78rem',
    color: 'var(--line-strong)',
    fontWeight: 'bold',
    minWidth: '26px',
    paddingTop: '3px',
    flexShrink: 0,
  },
  refIdBadge: {
    fontFamily: "'Consolas', 'Monaco', monospace",
    fontSize: '0.68rem',
    color: 'var(--line-strong)',
    background: 'var(--bg)',
    border: '1px solid var(--line)',
    borderRadius: '3px',
    padding: '1px 5px',
    flexShrink: 0,
    alignSelf: 'center',
    letterSpacing: '0.3px',
  },
  statusBadge: (color, bg) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 9px',
    borderRadius: '12px',
    fontSize: '0.76rem',
    fontWeight: '700',
    color,
    background: bg,
    border: `1px solid ${color}`,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  }),
  questionPreview: {
    fontSize: '0.92rem',
    color: 'var(--ink)',
    lineHeight: '1.45',
    flex: 1,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  chevron: {
    fontSize: '0.7rem',
    color: 'var(--line-strong)',
    flexShrink: 0,
    paddingTop: '4px',
  },

  // ── Expanded detail ───────────────────────────────────────────────────────────
  reviewDetail: {
    padding: '0 14px 16px 50px',
    borderTop: '1px solid var(--line)',
  },
  fullQuestion: {
    fontSize: '0.93rem',
    color: 'var(--ink)',
    lineHeight: '1.6',
    margin: '12px 0',
  },
  answerGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    fontSize: '0.875rem',
  },
  answerRow: {
    display: 'flex',
    gap: '8px',
  },
  answerRowLabel: {
    color: 'var(--muted)',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    minWidth: '110px',
    flexShrink: 0,
  },
  answerRowValue: (color) => ({
    color: color || 'var(--ink)',
    fontWeight: color ? '600' : 'normal',
    lineHeight: '1.45',
  }),
};

// ── Main component ────────────────────────────────────────────────────────────

export default function ResultsPage({
  session,
  questionStates,
  skippedIndices,
  completionReason,
  onReset,
  onRetake,
  onReport,
  onStartTargetedQuiz,
  onAnotherPractice,
  flaggedIndices,
  examElapsedSeconds,
  sessionStart,
  onRetrySessionStart,
  timeUsedSeconds,
  onFilterChange,
}) {
  // PR 2A: disable the session-start triggers (another practice / weak-area quiz)
  // while a cert bank is loading for a start.
  const starting = !!sessionStart && sessionStart.status === 'loading';
  const [showTargetedModal, setShowTargetedModal] = useState(false);
  const [savingDate, setSavingDate]       = useState(false);
  const [dateSaved, setDateSaved]         = useState(false);

  const { user, profile, refresh } = useLearnerAuth();

  const results       = computeResults(session.questions, questionStates, skippedIndices);
  // The review list is the SAME component (and the same normalized model) the
  // reopenable historical review renders — one review experience, two sources.
  const reviewModel   = buildLiveReviewModel({
    session, results, questionStates, flaggedIndices, completionReason,
    timeUsedSeconds,
  });
  const reasonCfg     = REASON_CFG[completionReason] ?? REASON_CFG.completed;
  const phaseMetrics  = computePhaseMetrics(results.items, session.examId);
  const phaseInsights = generateInsights(phaseMetrics);

  // Time-management analytics (Intelligent Pace Coaching §6). Only for the
  // timed modes pacing applies to; null otherwise (e.g. untimed practice).
  const paceCfg = getPaceConfig(session.examId);
  // Respect the learner's pace-coaching preferences exactly like the live
  // in-exam gate — without this the results analytics showed even when pacing
  // was turned off (e.g. a Targeted Test with the toggle off).
  const pacePrefEnabled = profile ? profile.pace_coaching_enabled !== false : true;
  const paceTargetedOptIn = profile ? profile.pace_coaching_in_targeted === true : false;
  const showPace = isPaceEnabled({
    paceConfig: paceCfg,
    mode: session.mode,
    prefEnabled: pacePrefEnabled,
    targetedOptIn: paceTargetedOptIn,
  });
  const paceAnalytics = showPace
    ? computePaceAnalytics(session, questionStates, {
        flaggedCount: flaggedIndices?.size ?? 0,
        elapsedSeconds: examElapsedSeconds,
      })
    : null;
  const weakPhases    = getWeakPhases(phaseMetrics);

  const examConfig        = getExamConfig(session.examId);
  const targetedQuizCount = examConfig?.targetedQuiz?.defaultQuestions ?? 15;

  // Only offer targeted quiz when: callback provided, weak phases exist,
  // and this isn't already a targeted session (avoid recursion).
  const showTargetedButton =
    !!onStartTargetedQuiz &&
    weakPhases.length > 0 &&
    session.mode !== 'targeted';
  const scoreColor =
    results.percentage >= 80 ? 'var(--good)'
    : results.percentage >= 60 ? 'var(--warn)'
    : 'var(--bad)';

  // Feedback banner colors mirror the score color bands
  const feedbackBg =
    results.percentage >= 80 ? 'var(--good-soft)'
    : results.percentage >= 60 ? 'var(--warn-soft)'
    : 'var(--bad-soft)';

  const feedbackMessage = getPerformanceFeedback(results.percentage);

  // ── Tiered mascot reaction (interaction map #6/#7) ───────────────────────────
  // PM Pixel reacts to the result, tiered so each pose stays meaningful:
  //   ≥ 80% (the "ready" band)  → cheer         (notable milestone)
  //   60–79%                    → thumbsup      (routine positive result)
  //   < 60%                     → contemplative (calm "let's review" coaching —
  //                               the portrait/emphasis pose; never celebratory,
  //                               paired with the constructive feedback message)
  // Only when there were scored questions, so a 0% all-experimental session
  // doesn't trigger a reaction.
  const feedbackPose =
    results.total === 0 ? null
    : results.percentage >= 80 ? 'cheer'
    : results.percentage >= 60 ? 'thumbsup'
    : 'contemplative';

  // ── Post-baseline next step (interaction map: "what now?") ───────────────────
  // After a signed-in learner finishes a scored practice/full session for a cert
  // that has no target exam date yet, offer two calm next steps: set an exam date
  // (default ~3 months out) or take another practice exam to deepen the picture.
  const recommendedDate = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return d;
  })();
  const recommendedIso   = recommendedDate.toISOString().slice(0, 10);
  const recommendedLabel = recommendedDate.toLocaleDateString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric',
  });
  const hasTargetDateForCert =
    profile?.target_exam_id === session.examId && !!profile?.target_exam_date;
  const showNextStep =
    !!user &&
    results.total > 0 &&
    session.mode !== 'targeted' &&
    !hasTargetDateForCert;

  async function handleSetExamDate() {
    if (!user?.id || savingDate) return;
    setSavingDate(true);
    await updateLearnerProfile(user.id, {
      target_exam_id:   session.examId,
      target_exam_date: recommendedIso,
    });
    await refresh?.();
    setSavingDate(false);
    setDateSaved(true);
  }

  // ── Subtitle beneath the heading ────────────────────────────────────────────
  function reasonSubtitle() {
    if (completionReason === 'expired') {
      return `Your session ended after ${session.durationMinutes} minutes.`;
    }
    if (completionReason === 'early') {
      return 'You ended the session early.';
    }
    return `You completed all ${session.totalQuestions} questions.`;
  }

  // Hero-card body (everything except the avatar). Rendered on its own when
  // there's no scored result, or inside CoachLayout (avatar beside it) when
  // there is a feedback pose.
  const heroBody = (
    <>
      <div style={styles.heroIcon}>{reasonCfg.icon}</div>
      <h2
        data-testid="results-heading"
        style={styles.heroHeading(reasonCfg.color)}
      >
        {reasonCfg.text}
      </h2>
      <p style={styles.heroMeta}>
        <strong>{session.displayName}</strong> · {session.modeName}
      </p>
      <p style={{ color: 'var(--muted)', fontSize: '0.88rem', margin: '0 0 20px' }}>
        {reasonSubtitle()}
      </p>

      {/* Score circle */}
      <div style={styles.scoreCircle(scoreColor)}>
        <span
          data-testid="results-score"
          style={styles.scoreCircleText(scoreColor)}
        >
          {results.percentage}%
        </span>
      </div>

      {/* Performance feedback */}
      <div
        data-testid="results-feedback"
        style={styles.feedbackBanner(scoreColor, feedbackBg)}
      >
        {feedbackMessage}
      </div>

      {results.hasExperimental && (
        <div
          data-testid="results-unscored-note"
          style={{
            fontSize: '0.78rem',
            color: 'var(--muted)',
            fontStyle: 'italic',
            marginTop: '12px',
            lineHeight: 1.4,
          }}
        >
          Some questions may be unscored and used for quality calibration.
        </div>
      )}
    </>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div data-testid="results-page" style={styles.page} className={rcss.page}>
      {showTargetedModal && (
        <TargetedQuizModal
          weakPhases={weakPhases}
          examId={session.examId}
          questionCount={targetedQuizCount}
          onStart={() => {
            setShowTargetedModal(false);
            onStartTargetedQuiz(weakPhases);
          }}
          onClose={() => setShowTargetedModal(false)}
        />
      )}
      <div style={styles.container} className={rcss.container}>

        {/* ── Screen header (standard ScreenHeader — Study Plan conventions) ── */}
        <ScreenHeader
          variant="banner"
          onBack={onReset}
          backLabel="Return to Home"
          backTestId="results-return-home"
          eyebrow={`${session.displayName} · ${session.modeName}`}
          title="Session Results"
          promo={<DiscordPromo />}
          actions={(
            <>
              {onRetake && (
                <button
                  data-testid="results-retake-exam"
                  style={styles.retakeButton}
                  onClick={onRetake}
                >
                  Retake Exam
                </button>
              )}
              {showTargetedButton && (
                <button
                  data-testid="results-targeted-quiz"
                  style={styles.targetedButton}
                  disabled={starting}
                  onClick={() => { if (!starting) setShowTargetedModal(true); }}
                >
                  🎯 Practice Weak Areas
                </button>
              )}
            </>
          )}
        />

        {sessionStart && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <SessionStartStatus
              status={sessionStart.status}
              error={sessionStart.error}
              canRetry={sessionStart.canRetry}
              onRetry={onRetrySessionStart}
            />
          </div>
        )}

        {/* ── Score hero card ────────────────────────────────────────────────── */}
        {/* Two-column coach layout: PM Pixel beside the result summary. The
            score circle / feedback stay centered within their column. When
            there are no scored questions, render the body alone (no avatar). */}
        <div style={styles.heroCard} className={rcss.heroCard}>
          {feedbackPose ? (
            <CoachLayout
              pose={feedbackPose}
              heightPx={feedbackPose === 'contemplative' ? 250 : 540}        // cheer/thumbsup at 540; contemplative (square sprite) at 250
              contentAlign="center"
            >
              {heroBody}
            </CoachLayout>
          ) : (
            heroBody
          )}
        </div>

        {/* ── Post-baseline next step ───────────────────────────────────────── */}
        {showNextStep && (
          <div
            data-testid="results-next-step"
            style={{
              border: '1px solid var(--brand-soft)',
              background: 'var(--brand-soft)',
              borderRadius: 12,
              padding: '18px 20px',
              margin: '0 0 20px',
            }}
          >
            {dateSaved ? (
              <p
                data-testid="results-next-step-confirm"
                style={{ margin: 0, color: 'var(--brand-deep)', fontSize: '0.95rem' }}
              >
                Exam date set for <strong>{recommendedLabel}</strong>. You can fine-tune
                it anytime in your Study Plan.
              </p>
            ) : (
              <>
                <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem', color: 'var(--ink)' }}>
                  Nice work — what’s next?
                </h3>
                <p style={{ margin: '0 0 14px', fontSize: '0.9rem', color: 'var(--muted)' }}>
                  Lock in a target date to start a study plan, or keep building depth
                  with another practice exam.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <button
                      type="button"
                      data-testid="results-set-exam-date"
                      onClick={handleSetExamDate}
                      disabled={savingDate}
                      style={{
                        padding: '0.6rem 1rem', fontWeight: 600, fontSize: '0.92rem',
                        background: 'var(--brand)', color: '#fff', border: 0,
                        borderRadius: 8, cursor: savingDate ? 'default' : 'pointer',
                      }}
                    >
                      {savingDate ? 'Setting…' : `Set my exam date — ${recommendedLabel}`}
                    </button>
                    <span style={{ marginTop: 4, fontSize: '0.78rem', color: 'var(--muted)' }}>
                      ≈3 months out · adjust anytime in your Study Plan
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <button
                      type="button"
                      data-testid="results-another-practice"
                      disabled={starting}
                      onClick={() => { if (starting) return; return onAnotherPractice ? onAnotherPractice() : onRetake?.(); }}
                      style={{
                        padding: '0.6rem 1rem', fontWeight: 600, fontSize: '0.92rem',
                        background: '#fff', color: 'var(--brand)',
                        border: '1px solid var(--brand)', borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      Take another practice exam
                    </button>
                    <span style={{ marginTop: 4, fontSize: '0.78rem', color: 'var(--muted)' }}>
                      Deepen the assessment with more questions
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Stat cards (4-col: correct / incorrect / skipped / unanswered) ── */}
        <div style={styles.statsGrid} className={rcss.statsGrid}>
          <div style={styles.statCard('var(--good)')}>
            <div
              data-testid="results-correct-count"
              style={styles.statNumber('var(--good)')}
            >
              {results.correct}
            </div>
            <div style={styles.statLabel}>Correct</div>
          </div>

          <div style={styles.statCard('var(--bad)')}>
            <div
              data-testid="results-incorrect-count"
              style={styles.statNumber('var(--bad)')}
            >
              {results.incorrect}
            </div>
            <div style={styles.statLabel}>Incorrect</div>
          </div>

          <div style={styles.statCard('var(--warn)')}>
            <div
              data-testid="results-skipped-count"
              style={styles.statNumber('var(--warn)')}
            >
              {results.skipped}
            </div>
            <div style={styles.statLabel}>Skipped</div>
          </div>

          <div style={styles.statCard('var(--muted)')}>
            <div
              data-testid="results-unanswered-count"
              style={styles.statNumber('var(--muted)')}
            >
              {results.unanswered}
            </div>
            <div style={styles.statLabel}>Unanswered</div>
          </div>
        </div>

        {/* ── Phase / Domain performance ───────────────────────────────────── */}
        <PhasePerformance
          metrics={phaseMetrics}
          insights={phaseInsights}
          examId={session.examId}
        />

        {/* ── Time management (Intelligent Pace Coaching) ──────────────────── */}
        {paceAnalytics && paceAnalytics.answered > 0 && (
          <PaceAnalytics analytics={paceAnalytics} />
        )}

        {/* ── Question-by-question review (shared with historical review) ─── */}
        <SessionReviewList
          items={reviewModel.items}
          counts={{
            total: results.total,
            correct: results.correct,
            incorrect: results.incorrect,
            skipped: results.skipped,
            unanswered: results.unanswered,
          }}
          caseBlocks={session.caseBlocks}
          caseStats={caseScoreStats(results.items)}
          onReport={onReport}
          onFilterChange={onFilterChange}
        />

      </div>
    </div>
  );
}
